/**
 * KAN-727: remembered upload settings of the list upload form.
 *
 * On open the latest preset of the list kind is applied once (mapping, limit,
 * checkbox; the link itself is only offered, not filled). `applyPreset` also
 * serves a picked recent link. `savedMapping` is the mapping of the preset in
 * use: it is the first choice when the real headers are matched.
 * KAN-826: the same for the organization columns and the custom fields
 * (`savedCompanyFields`, `savedExtraFields`); "update existing" is never saved.
 * KAN-850: a preset attached to THIS list (the copy "Следующая партия" makes)
 * wins over the latest one of the kind, and its Google Sheets link is filled
 * too (only while the link field is empty and no file is chosen; the user may
 * clear it). A preset of another list still only offers its link.
 * KAN-861: "skip existing clients" is not taken from a preset: it is on by
 * default and stays as the user set it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { UploadOptionsValue } from '@/components/datasets/UploadOptions';
import { useUploadPresets } from '@/hooks/useUploadSource';
import { recentSheetPresets } from '@/lib/uploadMapping';
import type {
  CampaignKind,
  ColumnMapping,
  CompanyFieldsMapping,
  ExtraFieldMapping,
  UploadPreset,
} from '@/types/dataset';

/** KAN-850: the preset of this list if there is one, else the latest of the kind. */
export function pickOpeningPreset(
  presets: UploadPreset[] | undefined,
  datasetId: string | undefined
): { preset: UploadPreset; ownList: boolean } | null {
  if (!presets || presets.length === 0) return null;
  const own = datasetId ? presets.find((p) => p.dataset_id === datasetId) : undefined;
  return own ? { preset: own, ownList: true } : { preset: presets[0], ownList: false };
}

export function useUploadPresetSetup(
  kind: CampaignKind | undefined,
  setOptions: Dispatch<SetStateAction<UploadOptionsValue>>,
  datasetId?: string,
  isFileChosen?: () => boolean
) {
  const [savedMapping, setSavedMapping] = useState<ColumnMapping>({});
  const [savedCompanyFields, setSavedCompanyFields] = useState<CompanyFieldsMapping>({});
  const [savedExtraFields, setSavedExtraFields] = useState<ExtraFieldMapping[]>([]);

  const applyPreset = useCallback(
    (preset: UploadPreset, sheetUrl?: string) => {
      setSavedMapping(preset.fields_mapping ?? {});
      setSavedCompanyFields(preset.company_fields ?? {});
      setSavedExtraFields(preset.extra_fields ?? []);
      setOptions((prev) => ({
        sheetUrl: sheetUrl ?? prev.sheetUrl,
        limit: preset.limit ? String(preset.limit) : '',
        // KAN-861: always on by default, a preset does not turn it off; the
        // user may still uncheck it for one upload.
        skipExistingClients: prev.skipExistingClients,
      }));
    },
    [setOptions]
  );

  const { data: presets } = useUploadPresets(kind);
  const opening = useMemo(() => pickOpeningPreset(presets, datasetId), [presets, datasetId]);
  const fileChosenRef = useRef(isFileChosen);
  fileChosenRef.current = isFileChosen;
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!opening || prefilledRef.current) return;
    prefilledRef.current = true;
    applyPreset(opening.preset);
    const link = opening.preset.sheet_url;
    if (opening.ownList && link && !fileChosenRef.current?.()) {
      // Functional update: never overwrite a link the user already typed.
      setOptions((prev) => (prev.sheetUrl.trim() ? prev : { ...prev, sheetUrl: link }));
    }
  }, [opening, applyPreset, setOptions]);

  const recentSheets = useMemo(() => recentSheetPresets(presets), [presets]);

  return { savedMapping, savedCompanyFields, savedExtraFields, applyPreset, recentSheets };
}
